/**
 * AiTab — natural-language odds query console (nlOdds test bench).
 * Extracted verbatim from AdminScreen.jsx (god-file decomposition).
 */
import React, { useEffect, useState } from 'react';
import { View, Text, TouchableOpacity, TextInput, ScrollView, Platform } from 'react-native';
import LegBetCard from './LegBetCard';
import { adminStyles as styles } from './adminStyles';

export default function AiTab() {
  const [prompt, setPrompt] = useState('');
  const [sport, setSport] = useState('');
  const [league, setLeague] = useState('');
  const [date, setDate] = useState('');
  const [sportsbooks, setSportsbooks] = useState('');
  const [booksList, setBooksList] = useState([]);
  const [booksOpen, setBooksOpen] = useState(false);
  const [market, setMarket] = useState('');
  const [marketsList, setMarketsList] = useState([]);
  const [marketsOpen, setMarketsOpen] = useState(false);
  const [output, setOutput] = useState('');
  const [loading, setLoading] = useState(false);
  const [friendly, setFriendly] = useState([]);
  const [endpoints, setEndpoints] = useState([]);
  const [queries, setQueries] = useState([]);
  const [hasRun, setHasRun] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await runOpticOddsRequest('/sportsbooks');
        const arr = Array.isArray(r?.body?.data) ? r.body.data : (Array.isArray(r?.body) ? r.body : (Array.isArray(r?.body?.items) ? r.body.items : []));
        const names = arr.map((b) => b?.name || b).filter(Boolean).sort();
        setBooksList(names);
      } catch {}
    })();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const qSport = sport || 'basketball';
        const r = await runOpticOddsRequest(`/markets?sport=${encodeURIComponent(qSport)}`);
        const arr = Array.isArray(r?.body?.data) ? r.body.data : (Array.isArray(r?.body) ? r.body : (Array.isArray(r?.body?.items) ? r.body.items : []));
        const names = arr.map((m) => m?.name || m?.market || m).filter(Boolean);
        const dedup = Array.from(new Set(names)).sort();
        setMarketsList(dedup);
      } catch {}
    })();
  }, [sport]);

  const run = async () => {
    setLoading(true);
    setHasRun(true);
    setOutput('Mapping intent and fetching odds...');
    try {
      const effectiveDate = (date && date.trim()) ? date.trim() : undefined;
      // Show immediate placeholder in the preview while mapping/fetching
      setFriendly([]);
      setEndpoints([]);
      setQueries([{
        selection: 'Parsing…',
        selection_line: null,
        market: market || '—',
        date: effectiveDate || '',
        sport: sport || '—',
        league: league || '—'
      }]);
      const defaults = {
        sport: sport || undefined,
        league: league || undefined,
        date: effectiveDate,
        sportsbooks: sportsbooks || undefined,
        market: market || undefined,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        now: new Date().toISOString()
      };
      const r = await runNlOdds(prompt, defaults);
      setEndpoints(r?.body?.endpoints || []);
      const qList = Array.isArray(r?.body?.mapping?.queries) ? r.body.mapping.queries : (Array.isArray(r?.body?.results) ? r.body.results.map(x => x?.query).filter(Boolean) : []);
      setQueries(qList);
      const results = Array.isArray(r?.body?.results) ? r.body.results : [];
      const rendered = [];
      const toText = (v) => {
        if (v === null || v === undefined) return '';
        if (typeof v === 'object') {
          if (Array.isArray(v)) return v.map(toText).filter(Boolean).join(', ');
          return v.name || v.label || v.title || v.display || v.id || '';
        }
        return String(v);
      };
      const BOOK_LINKS = {
        DraftKings: 'https://sportsbook.draftkings.com/',
        FanDuel: 'https://sportsbook.fanduel.com/',
        Caesars: 'https://www.caesars.com/sportsbook',
        bet365: 'https://www.bet365.com/',
        BetMGM: 'https://sports.betmgm.com/en/sports',
        PointsBet: 'https://pointsbet.com/'
      };
      for (const res of results) {
        const rawItems = Array.isArray(res?.items) ? res.items : [];
        const items = rawItems.flatMap((it) => Array.isArray(it?.odds) ? it.odds : [it]);
        for (const o of items) {
          const line = toText(o.selection_line ?? o.line ?? o.points ?? '');
          const sel = toText(o.selection || o.name || o.team_name || o.player_name || '');
          const bookName = toText(o.sportsbook || o.book || '');
          const priceRaw = o.price ?? o.american_price ?? o.odds;
          const price = (typeof priceRaw === 'number' || typeof priceRaw === 'string') ? String(priceRaw) : '';
          const market = toText(o.market || o.market_name || o.group || '');
          const fixture = toText(o.fixture_id || o.game_id || '');
          const deep = (o?.urls && (o.urls?.offer || o.urls?.deeplink || o.urls?.event || o.urls?.market)) || BOOK_LINKS[bookName] || null;
          if (!market && !sel) continue;
          rendered.push({
            sportsbook: bookName,
            market,
            selection: sel,
            line,
            price,
            fixtureId: fixture,
            deepLink: deep,
            date: (res?.query?.date || '').toString(),
            sport: res?.query?.sport || '',
            league: res?.query?.league || ''
          });
        }
      }
      // Include Same Game Parlay (SGP) summary when present
      try {
        const sgp = r?.body?.sgp;
        const entries = Array.isArray(sgp?.request?.entries) ? sgp.request.entries : [];
        if (entries.length >= 2) {
          const legsCount = entries.length;
          const legsText = entries.map((e) => `${e.market}: ${e.name}`).join(' + ');
          const offered = typeof sgp?.math?.offered_price_american === 'number' ? sgp.math.offered_price_american : null;
          const ai = typeof sgp?.math?.ai_price_american === 'number' ? sgp.math.ai_price_american : null;
          const evp = typeof sgp?.math?.ev_percent_vs_ai === 'number' ? sgp.math.ev_percent_vs_ai : null;
          const bookList = Array.isArray(sgp?.request?.sportsbooks) ? sgp.request.sportsbooks : [];
          const book = offered !== null ? (bookList[0] || 'Sportsbook') : (bookList[0] || 'OpticOdds AI');
          rendered.unshift({
            sportsbook: book,
            market: `Same Game Parlay (${legsCount} legs)`,
            selection: legsText,
            line: '',
            price: offered ?? ai ?? null,
            fixtureId: sgp?.request?.fixture_id || '',
            deepLink: null,
            date: (Array.isArray(results) && results[0]?.query?.date) || (qList?.[0]?.date) || '',
            sport: (Array.isArray(results) && results[0]?.query?.sport) || (qList?.[0]?.sport) || '',
            league: (Array.isArray(results) && results[0]?.query?.league) || (qList?.[0]?.league) || '',
            // Attach computed EV% to help display in description formatting below
            evPercent: evp
          });
        }
      } catch {}
      setFriendly(rendered);
      setOutput(`NL → Odds ${r.status}${r.ok ? ' OK' : ' ERR'}\n` + JSON.stringify(r.body, null, 2));
    } catch (e) {
      setOutput(`Failed: ${e?.message || e}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <View style={styles.tabWrap}>
      <Text style={styles.sectionTitle}>bOPT AI (Natural Language → Odds)</Text>
      <Text style={styles.caption}>Describe the bet in plain English. We map it to OpticOdds and fetch odds.</Text>

      <View style={styles.card}>
        <View style={[styles.row, { marginBottom: 16 }]}>
          <Text style={styles.label}>Prompt</Text>
          <TextInput style={styles.input} value={prompt} onChangeText={setPrompt} placeholder="Team to win and player prop..." placeholderTextColor="rgba(255,255,255,0.5)" />
        </View>

        <Text style={styles.groupHeader}>Defaults</Text>
        <View style={styles.row}>
          <Text style={styles.label}>Sport</Text>
          <TextInput style={styles.input} value={sport} onChangeText={setSport} placeholder="basketball" placeholderTextColor="rgba(255,255,255,0.5)" />
        </View>
        <View style={styles.row}>
          <Text style={styles.label}>League</Text>
          <TextInput style={styles.input} value={league} onChangeText={setLeague} placeholder="nba" placeholderTextColor="rgba(255,255,255,0.5)" />
        </View>
        <View style={styles.row}>
          <Text style={styles.label}>Date</Text>
          <TextInput style={styles.input} value={date} onChangeText={setDate} placeholder="YYYY-MM-DD (defaults to today)" placeholderTextColor="rgba(255,255,255,0.5)" />
        </View>
        <View style={[styles.row, { alignItems: 'flex-start' }]}>
          <Text style={styles.label}>Market</Text>
          <View style={{ flex: 1 }}>
            <View style={[styles.input, { paddingVertical: 8 }]}> 
              <Text style={{ color: '#fff' }}>{market || 'Any (none selected)'}</Text>
            </View>
            <View style={{ flexDirection: 'row', gap: 8, marginTop: 8 }}>
              <TouchableOpacity style={styles.smallBtn} onPress={() => setMarketsOpen(v => !v)}>
                <Text style={styles.smallBtnText}>{marketsOpen ? 'Hide Markets' : 'Select Markets'}</Text>
              </TouchableOpacity>
            </View>
            {marketsOpen ? (
              <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginTop: 8 }}>
                {marketsList.map((m) => {
                  const selected = market === m;
                  return (
                    <TouchableOpacity key={m} style={[styles.tag, selected ? styles.tagActive : null]} onPress={() => setMarket(selected ? '' : m)}>
                      <Text style={[styles.tagText, selected ? styles.tagTextActive : null]}>{m}</Text>
                    </TouchableOpacity>
                  );
                })}
              </View>
            ) : null}
          </View>
        </View>

        <View style={[styles.row, { alignItems: 'flex-start' }]}>
          <Text style={styles.label}>Sportsbooks</Text>
          <View style={{ flex: 1 }}>
            <View style={[styles.input, { paddingVertical: 8 }]}> 
              <Text style={{ color: '#fff' }}>{sportsbooks || 'All (none selected)'}</Text>
            </View>
            <View style={{ flexDirection: 'row', gap: 8, marginTop: 8 }}>
              <TouchableOpacity style={styles.smallBtn} onPress={() => setBooksOpen(v => !v)}>
                <Text style={styles.smallBtnText}>{booksOpen ? 'Hide Sportsbooks' : 'Select Sportsbooks'}</Text>
              </TouchableOpacity>
            </View>
            {booksOpen ? (
              <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginTop: 8 }}>
                {booksList.map((b) => {
                  const selected = (sportsbooks || '').split(',').filter(Boolean).includes(b);
                  return (
                    <TouchableOpacity key={b} style={[styles.tag, selected ? styles.tagActive : null]} onPress={() => {
                      const cur = (sportsbooks || '').split(',').filter(Boolean);
                      const idx = cur.indexOf(b);
                      if (idx >= 0) cur.splice(idx, 1); else cur.push(b);
                      setSportsbooks(cur.join(','));
                    }}>
                      <Text style={[styles.tagText, selected ? styles.tagTextActive : null]}>{b}</Text>
                    </TouchableOpacity>
                  );
                })}
              </View>
            ) : null}
          </View>
        </View>

        <View style={[styles.row, { flexWrap: 'wrap', gap: 8, marginTop: 8 }]}>
          <TouchableOpacity style={styles.btn} disabled={loading} onPress={run}>
            <Text style={styles.btnText}>{loading ? 'Running…' : 'Run bOPT AI'}</Text>
          </TouchableOpacity>
          <TouchableOpacity style={styles.btn} disabled={loading} onPress={() => setPrompt('Find best parlays today with top books')}>
            <Text style={styles.btnText}>Try: Best Parlays Today</Text>
          </TouchableOpacity>
          <TouchableOpacity style={styles.btn} disabled={loading} onPress={() => setPrompt('Make me the best 30% NBA SGP on FanDuel tonight')}>
            <Text style={styles.btnText}>Try: Make me the best 30% NBA SGP on FanDuel tonight</Text>
          </TouchableOpacity>
          <TouchableOpacity style={styles.btn} disabled={loading} onPress={() => setPrompt('Lakers -3.5 and LeBron over 9.5 assists tomorrow')}>
            <Text style={styles.btnText}>Try: Lakers -3.5 + LeBron A</Text>
          </TouchableOpacity>
          <TouchableOpacity style={styles.btn} disabled={loading} onPress={() => setPrompt('Guardians moneyline and Ramirez to record a hit')}>
            <Text style={styles.btnText}>Try: Guardians ML + Ramirez hit</Text>
          </TouchableOpacity>
        </View>
      </View>

      {hasRun ? (
        <View style={[styles.card, { marginTop: 12 }]}> 
          <Text style={styles.groupHeader}>Available Bets</Text>
          {loading ? (
            <Text style={styles.caption}>Loading available bets…</Text>
          ) : friendly.length ? (
            <View style={{ gap: 12 }}>
              {friendly.map((f, idx) => (
                <LegBetCard
                  key={`${f.fixtureId || idx}-${idx}`}
                  option={{
                    title: `${f.market}`,
                    description: `${f.selection}${f.line ? ` ${f.line}` : ''} • ${f.sportsbook}${f.price ? ` • ${f.price}` : ''}`,
                    amount: f.date || ''
                  }}
                  index={idx}
                  isSelected={false}
                  userSelectedSportsbooks={[]}
                  onCardPress={() => {
                    if (f.deepLink && Platform.OS === 'web') {
                      try { window.open(f.deepLink, '_blank'); } catch {}
                    }
                  }}
                />
              ))}
            </View>
          ) : (
            <Text style={styles.caption}>No bets found. Try a different market, date, or sportsbooks.</Text>
          )}
        </View>
      ) : null}

      <ScrollView style={styles.outputBox}>
        <Text style={styles.outputText}>{output}</Text>
      </ScrollView>

      {hasRun ? (
        <View style={[styles.card, { marginTop: 12 }]}> 
          <Text style={styles.groupHeader}>Endpoints Called</Text>
          {endpoints.length ? endpoints.map((e) => (
            <Text key={e} style={styles.caption}>{e}</Text>
          )) : (
            <Text style={styles.caption}>No endpoints yet.</Text>
          )}
        </View>
      ) : null}
    </View>
  );
}

